import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "./Preview.css";
import { useConstants } from "../../constant/constantProvider";
import { toast } from "react-toastify";

// assets
import download from "../../assets/images/downloadIcon.svg";
import prevIcon from "../../assets/new-images/previous-icon.svg";
import nextIcon from "../../assets/new-images/next-icon.svg";
import zoomout from "../../assets/images/zoomOut.svg";
import zoomIn from "../../assets/images/zoomIn.svg";
import close from "../../assets/images/previewClose.svg";

import { getFileNameFromUrl } from "../../utils/fileHelper";

const PdfPreview = ({ selectedFile, isOpen, onClose, isMobile }) => {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
  const pdfUrlFile = selectedFile?.path || selectedFile?.url;
  const pdfName = selectedFile?.filename || selectedFile?.name || getFileNameFromUrl(selectedFile?.path) || "document.pdf";

  const { DOWNLOAD } = useConstants();

  const [pdfUrl, setPdfUrl] = useState(pdfUrlFile);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setScale(isMobile ? 0.5 : 1.0);
      setPdfUrl(pdfUrlFile);
      setNumPages(null);
      setPageNumber(1);
      setLoading(selectedFile?.isLoading ?? !!pdfUrlFile);
      setLoadFailed(false);
    }
  }, [isOpen, isMobile, pdfUrlFile, selectedFile]);

  // Close modal with Escape
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.body.classList.add("policy-open");
    } else {
      document.body.classList.remove("policy-open");
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.body.classList.remove("policy-open");
    };
  }, [isOpen, onClose]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setLoadFailed(false);
  };

  const onDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
    toast.error("Failed to load PDF.");
    setLoading(false);
    setLoadFailed(true);
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 5));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  const handleDownload = async () => {
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pdfName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download PDF. Please try again.");
    }
  };

  return (
    <div className={`pdf-modal-overlay ${isOpen ? "open" : ""}`}>
      <div className="pdf-modal" ref={modalRef}>
        <div className="pdf-modal-header">
          <div className="preview-title">{pdfName}</div>
          <div className="pdf-header-buttons">
            <button onClick={handleZoomIn} className="icon-button" title="Zoom In">
              <img src={zoomIn} alt="Zoom In" />
            </button>
            <button onClick={handleZoomOut} className="icon-button" disabled={scale <= 0.5} title="Zoom Out">
              <img src={zoomout} alt="Zoom Out" />
            </button>
            {!isMobile && (
              <button className="button-style download-button primary-button" onClick={handleDownload}>
                <img src={download} alt="Download Icon" />
                {DOWNLOAD}
              </button>
            )}
            <button className="close-button" onClick={onClose} title="Close">
              <img src={close} alt="Close" />
            </button>
          </div>
        </div>

        {/* Scrollable PDF content */}
        <div className="pdf-scrollable-wrapper">
          <div className="pdf-viewer-container-preview">
            {loadFailed ? (
              <p className="pdf-error-message">Failed to load PDF file.</p>
            ) : (
              pdfUrl && (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading=""
                >
                  <Page pageNumber={pageNumber} scale={scale} />
                </Document>
              )
            )}
          </div>
        </div>

        {/* Fixed footer */}
        {pdfUrl && numPages && !loading && !loadFailed && (
          <div className="page-number-footer">
            <div className="page-navigation-footer">
              <button
                className="nav-arrow"
                onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                disabled={pageNumber === 1}
                title="Previous page"
              >
                <img src={prevIcon} alt="prev" />
              </button>

              <div className="page-number-display">{pageNumber}</div>

              <button
                className="nav-arrow"
                onClick={() =>
                  setPageNumber((prev) => Math.min(prev + 1, numPages))
                }
                disabled={pageNumber === numPages}
                title="Next page"
              >
                <img src={nextIcon} alt="next" />
              </button>
            </div>
          </div>
        )}

        {loading && !loadFailed && (
          <div className="pdf-loader">
            <div className="spinner-loader"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPreview;
